import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { User } from "firebase/auth";
import Firebase from "./api/firebase.class";
import AppUser, { GeneratedImgInfo, ImgInfo } from "./api/app-user.class";
import { Comic, FirestoreComicObj } from "./classes/comic.class";

// MODO VITRINE — a trava, e por que ela e escrita exatamente assim.
//
// `react-scripts` substitui `process.env.NODE_ENV` e `process.env.REACT_APP_*` por LITERAIS de
// string em tempo de build (DefinePlugin). Num build de producao a condicao abaixo vira
// `"production" !== "production" && ...`, que o parser do webpack avalia como falsa ANTES de
// montar o grafo de modulos — e um `require` dentro de um ramo que o parser ja sabe morto nunca
// vira dependencia. O modulo `vitrine/vitrine` simplesmente nao entra no pacote.
//
// A PRIMEIRA VERSAO DISTO ERRAVA, e o erro e instrutivo: eu exportava `vitrineLigada` de
// `vitrine/vitrine` e escrevia `vitrineLigada ? <Vitrine/> : <Real/>`. O terser do CRA roda SEM
// `toplevel`, entao ele dobra a constante para `!1` mas nao a propaga para dentro do ternario.
// Resultado: o ramo sobrevivia e o usuario falso ia inteiro para o `main.js` publicado — medido,
// 1 ocorrencia de `vitrine@exemplo.invalido`. A garantia de COMPORTAMENTO existia (a const e
// `false` e nao ha como reverte-la em runtime); a de AUSENCIA DO CODIGO, nao. Sao coisas
// diferentes, e so a segunda e auditavel por quem nao le o codigo.
//
// Por isso a condicao literal aparece aqui, e nao atras de uma constante importada. Nao troque
// por uma variavel: a prova em `scripts/prova-vitrine.mjs` quebra.
type ModuloDeVitrine = typeof import("./vitrine/vitrine");

// O valor e montado AQUI DENTRO, e nao numa funcao chamada depois, por duas razoes. A primeira e
// a prova: qualquer identificador da vitrine mencionado fora deste ramo sobrevive ao build (o
// terser do CRA nao remove funcao morta de topo de modulo), e a medicao deixa de dar zero. A
// segunda e comportamental: montado uma vez, o usuario falso tem identidade ESTAVEL entre
// renders — fabricado a cada render, ele seria um objeto novo toda vez e faria as telas que
// dependem dele em `useEffect` girarem sem parar.
let valorDeVitrine: GlobalState | null = null;
if (
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_VITRINE === "sim"
) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const v = require("./vitrine/vitrine") as ModuloDeVitrine;
  valorDeVitrine = {
    authUser: v.authUserDeVitrine() as unknown as User,
    appUser: v.usuarioDeVitrine(),
  };
}

export type GlobalState = {
  authUser: User | null;
  appUser: AppUser | null;
};

const GlobalStateContext = createContext<GlobalState | null>(null);

export const useGlobalState = () => useContext(GlobalStateContext);

type GlobalStateProviderProps = {
  children: ReactNode;
};

// O desvio fica AQUI, neste provider, porque ele e o unico lugar em que `authUser` e `appUser`
// nascem: toda tela os le por `useGlobalState()`. Interceptar num ponto so significa que nenhuma
// tela precisa saber que a vitrine existe — e telas que nao sabem de um modo de teste nao podem
// se comportar diferente por causa dele.
//
// Sao DOIS COMPONENTES IRMAOS, e nao um `return` antecipado, de proposito: o retorno antecipado
// deixaria os hooks do caminho normal abaixo de uma condicao, o que viola `rules-of-hooks` de
// verdade — nao so no olho do eslint. Como irmaos, cada um chama os proprios hooks
// incondicionalmente.
export function GlobalStateProvider(props: GlobalStateProviderProps) {
  if (valorDeVitrine) return <ProvedorVitrine {...props} />;
  return <ProvedorReal {...props} />;
}

function ProvedorVitrine({ children }: GlobalStateProviderProps) {
  return (
    <GlobalStateContext.Provider value={valorDeVitrine}>
      {children}
    </GlobalStateContext.Provider>
  );
}

function ProvedorReal({ children }: GlobalStateProviderProps) {
  const [globalState, setGlobalState] = useState<GlobalState | null>(null);

  const [authUser, setAuthUser] = useState<User | null | undefined>();
  const [appUser, setAppUser] = useState<AppUser | null | undefined>();
  const [comics, setComics] = useState<Comic[]>([]);
  const [userImgs, setUserImgs] = useState<
    Record<string, ImgInfo> | undefined
  >();
  const [publicImgs, setPublicImgs] = useState<
    Record<string, ImgInfo> | undefined
  >();
  const [imgs, setImgs] = useState<Record<string, ImgInfo> | undefined>();
  const [generatedImages, setGeneratedImages] = useState<
    Record<string, GeneratedImgInfo>
  >({});

  useEffect(() => {
    return Firebase.onAuthStateChanged((user) => {
      if (!!user && !user.emailVerified) {
        // Firebase.logOut();
        setAuthUser(null);
      } else {
        setAuthUser(user);
      }
    });
  }, []);
  useEffect(() => {
    if (!authUser) {
      setAppUser(authUser);
    } else {
      const unsubscribe = Firebase.onDocSnapshot(
        "users",
        authUser.uid,
        async (doc) => {
          const data = doc.data() as any;
          if (!data) setAppUser(null);
          else
            setAppUser(
              new AppUser({ ...data, comics: {}, imgs: {}, id: doc.id })
            );
        }
      );

      return () => unsubscribe();
    }
  }, [authUser]);
  useEffect(() => {
    if (!appUser || !imgs) {
      setComics([]);
      return;
    }

    const unsubscribe = Firebase.onCollectionSnapshot(
      `users/${appUser.Id()}/comics`,
      async (result) => {
        const _comics = result.docs.map((doc) =>
          Comic.fromFirestoreComicObj(
            doc.id,
            doc.data() as FirestoreComicObj,
            imgs
          )
        );
        setComics(_comics);
      }
    );

    return () => unsubscribe();
  }, [appUser, imgs]);

  useEffect(() => {
    if (!appUser) {
      setUserImgs(undefined);
      return;
    }

    const unsubscribe = Firebase.onCollectionSnapshot(
      `users/${appUser.Id()}/imgs`,
      async (result) => {
        const _imgs: Record<string, ImgInfo> = {};
        result.forEach((doc) => {
          _imgs[doc.id] = doc.data() as ImgInfo;
        });
        setUserImgs(_imgs);
      }
    );

    return () => unsubscribe();
  }, [appUser]);
  useEffect(() => {
    if (!appUser) {
      setPublicImgs(undefined);
      return;
    }

    const unsubscribe = Firebase.onCollectionSnapshot(
      `imgs`,
      async (result) => {
        const _imgs: Record<string, ImgInfo> = {};
        result.forEach((doc) => {
          _imgs[doc.id] = doc.data() as ImgInfo;
        });
        setPublicImgs(_imgs);
      }
    );

    return () => unsubscribe();
  }, [appUser]);
  useEffect(() => {
    if (!userImgs || !publicImgs) {
      setImgs(undefined);
    } else {
      setImgs({ ...userImgs, ...publicImgs });
    }
  }, [userImgs, publicImgs]);

  useEffect(() => {
    if (!appUser) {
      setGeneratedImages({});
      return;
    }

    const unsubscribe = Firebase.onCollectionSnapshot(
      `users/${appUser.Id()}/generatedImages`,
      async (result) => {
        const _generatedImages: Record<string, GeneratedImgInfo> = {};
        result.forEach((doc) => {
          _generatedImages[doc.id] = doc.data() as GeneratedImgInfo;
        });
        setGeneratedImages(_generatedImages);
      }
    );

    return () => unsubscribe();
  }, [appUser]);

  useEffect(() => {
    if (
      authUser === undefined ||
      appUser === undefined ||
      (!!appUser && !imgs)
    ) {
      setGlobalState(null);
    } else {
      if (!!appUser) {
        appUser.Comics(comics);
        appUser.Imgs(imgs);
        appUser.GeneratedImages(generatedImages);
      }

      setGlobalState({
        appUser,
        authUser,
      });
    }
  }, [authUser, appUser, comics, imgs, generatedImages]);

  return (
    <GlobalStateContext.Provider value={globalState}>
      {children}
    </GlobalStateContext.Provider>
  );
}
